import React from "react";
import { X } from "lucide-react";
import { HexColorPicker, HexColorInput } from "react-colorful";
import { PICKER_PRESETS } from "../../constants/palettes";

// `alwaysOpen`: no summary button — the presets and the canvas picker show
// straight away (the donut's slice-colour editor, which is a panel of its own).
// `pickerFallback`: where the canvas starts when no colour is set yet (default red).
export function ModalColorPicker({ currentColor, onColorChange, onSaveColor, savedColors, S, alwaysOpen = false, pickerFallback = "#ff0000" }) {
  const [panelOpenState, setPanelOpen] = React.useState(false);
  const panelOpen = alwaysOpen || panelOpenState;
  const [pickerOpen, setPickerOpen] = React.useState(alwaysOpen);
  const [hexPanelOpen, setHexPanelOpen] = React.useState(false);

  return (
    <div>
      {!alwaysOpen && <button onClick={() => setPanelOpen(v => !v)}
        style={{ display: "flex", alignItems: "center", gap: 6, padding: "5px 12px", borderRadius: 20, border: `1.5px solid ${currentColor || "#f0d0e8"}`, background: panelOpen ? "var(--c-bg-card)" : "transparent", color: "var(--c-text-accent)", fontSize: 14.5, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
        {currentColor ? <span style={{ width: 12, height: 12, borderRadius: "50%", background: currentColor, display: "block", border: "1px solid rgba(0,0,0,0.15)" }} /> : <span>🎨</span>}
        <span>{currentColor ? currentColor : "Set color"}</span>
      </button>}

      {panelOpen && (
        <div onPointerDown={e => e.stopPropagation()} onClick={e => e.stopPropagation()} style={{ marginTop: alwaysOpen ? 0 : 8, background: "var(--c-bg-card)", border: "1.5px solid #f0d0e8", borderRadius: 14, padding: "10px", display: "flex", flexDirection: "column", gap: 8 }}>
          {/* Presets */}
          <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
            <button onClick={() => onColorChange("")}
              style={{ width: 24, height: 24, borderRadius: "50%", border: !currentColor ? "2.5px solid #d4789a" : "1.5px solid #d0b8c8", background: "var(--c-bg-card)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, padding: 0 }}><X size={16} /></button>
            {PICKER_PRESETS.map(c => (
              <button key={c} onClick={() => onColorChange(c)}
                style={{ width: 24, height: 24, borderRadius: "50%", background: c, border: currentColor === c ? "2.5px solid #3a3040" : "1.5px solid transparent", cursor: "pointer", padding: 0, boxShadow: "0 1px 4px rgba(0,0,0,0.15)" }} />
            ))}
          </div>
          {/* Picker + Hex */}
          <div style={{ display: "flex", gap: 6 }}>
            <button onClick={() => { setPickerOpen(v => !v); setHexPanelOpen(false); }}
              style={{ flex: 1, padding: "6px 0", borderRadius: 10, border: "1.5px solid #f0d0e8", background: pickerOpen ? "var(--c-bg-card)" : "transparent", color: "var(--c-text-accent)", fontSize: 14.5, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>🎨 Picker</button>
            <button onClick={() => { setHexPanelOpen(v => !v); setPickerOpen(false); }}
              style={{ flex: 1, padding: "6px 0", borderRadius: 10, border: "1.5px solid #f0d0e8", background: hexPanelOpen ? "var(--c-bg-card)" : "transparent", color: "var(--c-text-accent)", fontSize: 14.5, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}># Hex</button>
          </div>
          {/* HexColorPicker — always in DOM, hidden via CSS */}
          <div style={{ display: pickerOpen ? "flex" : "none", flexDirection: "column", gap: 6, alignItems: "center" }}>
            <HexColorPicker color={currentColor || pickerFallback} onChange={(hex) => onColorChange(hex)} onMouseUp={() => {}} style={{ width: "100%" }} />
            {currentColor && <div style={{ display: "flex", alignItems: "center", gap: 6, alignSelf: "flex-start" }}>
              <div style={{ width: 22, height: 22, borderRadius: 6, background: currentColor, border: "1px solid #e0d0e0", flexShrink: 0 }} />
              <span style={{ fontSize: 11, color: "var(--c-text-soft)", fontWeight: 600 }}>{currentColor}</span>
            </div>}
          </div>
          {/* Hex input */}
          {hexPanelOpen && (
            <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
              <HexColorInput
                color={currentColor || "#000000"}
                onChange={onColorChange}
                prefixed
                placeholder="#RRGGBB"
                style={{ flex: 1, border: "1.5px solid #f0d0e8", borderRadius: 8, padding: "5px 8px", fontSize: 12, fontFamily: "monospace", outline: "none", background: "var(--c-bg-card)", color: "var(--c-text)", width: 0 }}
              />
              {currentColor && (
                <div style={{ width: 24, height: 24, borderRadius: 6, background: currentColor, border: "1px solid #e0d0e0", flexShrink: 0 }} />
              )}
            </div>
          )}
          {/* Saved colors */}
          {savedColors?.length > 0 && (
            <div>
              <span style={{ fontSize: 10, fontWeight: 700, color: "var(--c-text-soft)", textTransform: "uppercase", letterSpacing: "0.06em" }}>Saved</span>
              <div style={{ display: "flex", gap: 5, flexWrap: "wrap", marginTop: 4 }}>
                {savedColors.map((c, i) => (
                  <button key={i} onClick={() => onColorChange(c)}
                    style={{ width: 20, height: 20, borderRadius: "50%", background: c, border: currentColor === c ? "2.5px solid #3a3040" : "1.5px solid transparent", cursor: "pointer", padding: 0, boxShadow: "0 1px 4px rgba(0,0,0,0.15)" }} />
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
